import NotificationInput from "../../notifications/NotificationInput";
import { ActualWorkflowDataForTheDetailsPanelType } from "./ProcessTableDetailsPanel";
import SubscriberPillItem from "./SubscriberPillItem";
import { Button, IconButton } from "@avenueops/web-components";
import { PlusSmallIcon } from "@heroicons/react/24/outline";
import { DevTool } from "@hookform/devtools";
import { NotificationType } from "@prisma/client";
import GeneralModal from "components/GeneralModal";
import { QueryRunProvider } from "context/QueryRunContext";
import React, { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import Skeleton from "react-loading-skeleton";
import { toast } from "react-toastify";
import { trpc, trpc2 } from "utils/trpc";

interface Props {
  workflowProcess: ActualWorkflowDataForTheDetailsPanelType;
}

export default function SubscriberSection({ workflowProcess }: Props) {
  // React Hooks
  const [modalVisible, setModalVisible] = useState(false);

  // Queries and Mutations
  const utils = trpc.useContext();
  const { data: slackRecipientOptions, isLoading: loadingSlackrecipients } = trpc2.v2.slack.getRecipients.useQuery();

  const { mutateAsync } = trpc2.v2.subscribers.createSubscriberForProcess.useMutation({
    onSuccess: async () => {
      utils.processes.get.invalidate();
      setModalVisible(false);
    },
  });

  // React Hook Form Setup
  const formMethods = useForm({
    defaultValues: { subscriber: { manualType: NotificationType.SLACK, recipientValue: "", emailCCRecipient: null, mapping: null } },
    mode: "all",
  });

  const { control, handleSubmit, formState } = formMethods;
  const { isSubmitting } = formState;

  const notificationPrefix = "subscriber";

  const onSubmit = async (data: any) => {
    toast.promise(mutateAsync({ processId: workflowProcess.id, data: data.subscriber }), {
      pending: "Adding subscriber...",
      success: "Success 🎉",
      error: "There was an error adding the subscriber. Please try again later 😭",
    });
  };

  return (
    <QueryRunProvider workflowId={workflowProcess.workflowId}>
      <FormProvider {...formMethods}>
        <div className="flex flex-row">
          <div className="w-full flex flex-wrap gap-1 text-black font-medium text-ellipsis items-center">
            {workflowProcess?.subscribers?.map((subscriber, index) => {
              return <SubscriberPillItem key={subscriber.id} subscriber={subscriber} />;
            })}
            <Button LeadIcon={PlusSmallIcon} onClick={() => setModalVisible(true)} size="SMALL" type="SECONDARY">
              Add Subscriber
            </Button>
          </div>
        </div>

        {modalVisible && (
          <GeneralModal
            isLoading={isSubmitting}
            modalVisible={modalVisible}
            setModalVisible={setModalVisible}
            onCancel={() => setModalVisible(false)}
            onConfirm={handleSubmit(onSubmit)}
            confirmText={"Add"}
            title="Add a new signal subscriber"
            description="Stay on top of the signal and in sync with any updates by subscribing to it."
            noWidth={true}
            noMaxWidth={true}
            widthOverride={"w-[60vw] p-4 overflow-y-auto"}
          >
            {loadingSlackrecipients ? (
              <>
                <Skeleton count={2} />
              </>
            ) : (
              <NotificationInput notificationPrefix={notificationPrefix} />
            )}
          </GeneralModal>
        )}
        <DevTool placement="bottom-right" control={control} />
      </FormProvider>
    </QueryRunProvider>
  );
}
